import { useEffect, useRef } from "react";
import posthog from "posthog-js";
import { CONTACT_DOCUMENT_SOURCE } from "./contactDocumentSource";
import { compareShadowAnswers, SHADOW_SETTLE_MS } from "./contactDocumentRules";
import type { ContactDocumentShadowEvent, ShadowAnswer } from "./contactDocumentRules";
import type { ContactFetchTrigger } from "./contactDocumentStore";

/**
 * SHADOW MODE'S METER — what the plan's P1 verification reads.
 *
 * While `CONTACT_DOCUMENT_SOURCE` is 'shadow', every surface still gets its answer from the
 * Firestore listener, and the document store runs beside it. Each time BOTH have a definite
 * answer for the same contact, the two are compared on the fields the CRM row reads and the
 * result goes out as one PostHog event, `contact_document_shadow`, with the signal→answer
 * latency of the server side. Sampled 1:1 — a comparison happens once per settled change,
 * which is rare.
 *
 * The comparison waits {@link SHADOW_SETTLE_MS} after either side last moved: right after a
 * write the listener is ahead of the server path for the length of the mirror's lag window
 * (a signal, a fetch), and a mismatch inside that window is the design working, not a bug.
 * What must read 0 is the mismatch rate OUTSIDE it.
 */

export const SHADOW_EVENT = "contact_document_shadow";

export const recordContactDocumentShadowEvent = (event: ContactDocumentShadowEvent): void => {
  try {
    if (posthog.__loaded) {
      posthog.capture(SHADOW_EVENT, event);
    } else if (process.env.NODE_ENV === "development") {
      // eslint-disable-next-line no-console
      console.debug(SHADOW_EVENT, event);
    }
    if (!event.matched && process.env.NODE_ENV === "development") {
      // eslint-disable-next-line no-console
      console.debug(`${SHADOW_EVENT}: the listener and the server disagree`, event);
    }
  } catch {
    /* a meter must never fail the screen it measures */
  }
};

export type ShadowServerAnswer = ShadowAnswer & { lastFetch?: { trigger: ContactFetchTrigger; latencyMs: number } };

/**
 * Compares the listener's definite answer with the server path's, once both exist and have
 * held still for the settle window. Pass `undefined` for a side that has no definite answer
 * yet (resolving) — nothing is compared until both do. A no-op in every mode but 'shadow'.
 */
export function useContactDocumentShadow(
  source: ContactDocumentShadowEvent["source"],
  listener: ShadowAnswer | undefined,
  server: ShadowServerAnswer | undefined
): void {
  // The last pair compared, by identity: the same two answers are never reported twice.
  const compared = useRef<{
    listenerDoc: unknown;
    serverDoc: unknown;
    listenerExists: boolean;
    serverExists: boolean;
  } | null>(null);

  const listenerExists = listener?.exists;
  const listenerDoc = listener?.doc;
  const serverExists = server?.exists;
  const serverDoc = server?.doc;
  const lastFetch = server?.lastFetch;

  useEffect(() => {
    if (CONTACT_DOCUMENT_SOURCE !== "shadow") return undefined;
    if (listenerExists === undefined || serverExists === undefined) return undefined;

    const timer = setTimeout(() => {
      const last = compared.current;
      if (
        last &&
        last.listenerExists === listenerExists &&
        last.serverExists === serverExists &&
        last.listenerDoc === listenerDoc &&
        last.serverDoc === serverDoc
      ) {
        return;
      }
      compared.current = { listenerExists, serverExists, listenerDoc, serverDoc };

      const comparison = compareShadowAnswers(
        { exists: listenerExists, doc: listenerDoc ?? null },
        { exists: serverExists, doc: serverDoc ?? null }
      );
      recordContactDocumentShadowEvent({
        source,
        mode: CONTACT_DOCUMENT_SOURCE,
        matched: comparison.matched,
        differences: comparison.differences,
        latencyMs: lastFetch?.latencyMs ?? null,
        trigger: lastFetch?.trigger ?? null,
        settleMs: SHADOW_SETTLE_MS
      });
    }, SHADOW_SETTLE_MS);

    return () => clearTimeout(timer);
  }, [source, listenerExists, listenerDoc, serverExists, serverDoc, lastFetch]);
}
